/**
 * Industry-Standard Card Validation Utilities
 * 
 * Implements proper card number formatting, validation, and brand detection
 * following payment industry standards for Visa, Mastercard, Amex, Discover, etc.
 */

// Card brand configuration with industry-standard BIN ranges
export const CARD_BRANDS = {
  VISA: {
    name: 'Visa',
    pattern: /^4/,
    lengths: [16],
    cvvLength: 3,
    format: [4, 4, 4, 4] // XXXX XXXX XXXX XXXX
  },
  MASTERCARD: {
    name: 'Mastercard', 
    pattern: /^(5[1-5]|2[2-7])/,
    lengths: [16],
    cvvLength: 3,
    format: [4, 4, 4, 4] // XXXX XXXX XXXX XXXX
  },
  AMEX: {
    name: 'American Express',
    pattern: /^3[47]/,
    lengths: [15],
    cvvLength: 4,
    format: [4, 6, 5] // XXXX XXXXXX XXXXX
  },
  DISCOVER: {
    name: 'Discover',
    pattern: /^(6011|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[0-2]\d|92[0-5])|64[4-9]|65)/,
    lengths: [16],
    cvvLength: 3,
    format: [4, 4, 4, 4] // XXXX XXXX XXXX XXXX
  },
  DINERS: {
    name: 'Diners Club',
    pattern: /^(36|38|30[0-5])/,
    lengths: [14],
    cvvLength: 3,
    format: [4, 6, 4] // XXXX XXXXXX XXXX
  },
  JCB: {
    name: 'JCB',
    pattern: /^35(2[89]|[3-8]\d)/,
    lengths: [16],
    cvvLength: 3,
    format: [4, 4, 4, 4] // XXXX XXXX XXXX XXXX
  }
};

/**
 * Detect card brand from card number
 * @param {string} cardNumber - Raw card number (can include spaces)
 * @returns {object|null} Card brand configuration or null if not detected
 */
export const detectCardBrand = (cardNumber) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  for (const [brandKey, config] of Object.entries(CARD_BRANDS)) {
    if (config.pattern.test(cleanNumber)) {
      return { key: brandKey, ...config };
    }
  }
  
  return null;
};

/**
 * Format card number based on detected brand
 * @param {string} cardNumber - Raw card number
 * @returns {string} Formatted card number
 */
export const formatCardNumber = (cardNumber) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  const brand = detectCardBrand(cleanNumber);
  
  if (!brand) {
    // Default formatting for unrecognized cards (4-4-4-4)
    return cleanNumber
      .replace(/(.{4})/g, '$1 ')
      .trim()
      .slice(0, 23); // Max 19 digits + 4 spaces
  }
  
  // Apply brand-specific formatting
  const { format } = brand;
  let formatted = '';
  let currentIndex = 0;
  
  for (let i = 0; i < format.length; i++) {
    const groupLength = format[i];
    const group = cleanNumber.slice(currentIndex, currentIndex + groupLength);
    
    if (group.length > 0) {
      if (formatted.length > 0) {
        formatted += ' ';
      }
      formatted += group;
      currentIndex += groupLength;
    } else {
      break;
    }
  }
  
  return formatted;
};

/**
 * Validate card number length for detected brand
 * @param {string} cardNumber - Raw card number
 * @returns {boolean} True if length is valid for the detected brand
 */
export const isValidCardLength = (cardNumber) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  const brand = detectCardBrand(cleanNumber);
  
  if (!brand) {
    // Generic validation for unrecognized cards
    return cleanNumber.length >= 13 && cleanNumber.length <= 19;
  }
  
  return brand.lengths.includes(cleanNumber.length);
};

/**
 * Get maximum allowed length for card number input
 * @param {string} cardNumber - Current card number value
 * @returns {number} Maximum character length including spaces
 */
export const getMaxCardLength = (cardNumber) => {
  const brand = detectCardBrand(cardNumber);
  
  if (!brand) {
    return 23; // 19 digits + 4 spaces (default max)
  }
  
  const maxDigits = Math.max(...brand.lengths);
  const spacesCount = brand.format.length - 1;
  return maxDigits + spacesCount;
};

/**
 * Validate CVV based on detected card brand
 * @param {string} cvv - CVV value
 * @param {string} cardNumber - Card number to detect brand
 * @returns {boolean} True if CVV length is valid for the card brand
 */
export const isValidCvv = (cvv, cardNumber) => {
  const cleanCvv = cvv.replace(/\D/g, '');
  const brand = detectCardBrand(cardNumber);
  
  if (!brand) {
    // Generic validation: 3-4 digits for unknown cards
    return cleanCvv.length >= 3 && cleanCvv.length <= 4;
  }
  
  return cleanCvv.length === brand.cvvLength;
};

/**
 * Get expected CVV length for detected card brand
 * @param {string} cardNumber - Card number to detect brand
 * @returns {number} Expected CVV length
 */
export const getExpectedCvvLength = (cardNumber) => {
  const brand = detectCardBrand(cardNumber);
  return brand ? brand.cvvLength : 3; // Default to 3 for unknown cards
};

/**
 * Get CVV placeholder text based on card brand
 * @param {string} cardNumber - Card number to detect brand
 * @returns {string} Placeholder text for CVV field
 */
export const getCvvPlaceholder = (cardNumber) => {
  const brand = detectCardBrand(cardNumber);
  
  if (brand && brand.key === 'AMEX') {
    return '1234'; // 4-digit CVV for Amex
  }
  
  return '123'; // 3-digit CVV for all other cards
};

/**
 * Comprehensive card number validation
 * @param {string} cardNumber - Card number to validate
 * @returns {object} Validation result with details
 */
export const validateCardNumber = (cardNumber) => {
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  if (!cleanNumber) {
    return {
      isValid: false,
      error: 'Card number is required',
      brand: null
    };
  }
  
  const brand = detectCardBrand(cleanNumber);
  
  if (!brand) {
    if (cleanNumber.length < 13) {
      return {
        isValid: false,
        error: 'Card number is too short',
        brand: null
      };
    }
    
    if (cleanNumber.length > 19) {
      return {
        isValid: false,
        error: 'Card number is too long',
        brand: null
      };
    }
    
    return {
      isValid: false,
      error: 'Invalid card number',
      brand: null
    };
  }
  
  if (!isValidCardLength(cleanNumber)) {
    return {
      isValid: false,
      error: `${brand.name} card number must be ${brand.lengths.join(' or ')} digits`,
      brand
    };
  }
  
  return {
    isValid: true,
    error: null,
    brand
  };
};